import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { get } from 'lodash'
import { ErrorBoundary } from '@scoir/error-reporter'
import {
  LoadingState,
  ProfileHeader,
  ProfileRoutes,
  CollegeProfileContextWrapper,
  Footer,
  getIsProspectOnly,
} from '@scoir/college-details'

import permissions from 'common/system-permissions'
import usePermissions from 'common/use-permissions'
import { selectApplicationCycle } from 'store/application-cycle'
import {
  isCollegeUpdateRequestEnabled,
  isGoogleMapsFeatureEnabled,
  isTnTSearchFilterEnabled,
} from 'store/feature-toggle'
import useUserScid from 'common/use-user-scid'
import api from 'common/api'
import useTitle from 'common/use-title'
import { TOP_NAVIGATION_HEIGHT_ID } from 'components/HeaderNavigation'
import useLoadCollegeOnMount from './use-load-college-on-mount'
import sectionsConfiguration from './sections-configuration'
import RequestUpdateAction from './RequestUpdateAction'
import FooterContent from './FooterContent'

export const getSessions = (scid) =>
  api.post(`/api/colleges/sessions?from=0&sort=-CreatedDate${scid ? `&scid=${scid}` : ''}`, {})

export const getSimilarColleges = (scid) => api.get(`/api/colleges/${scid}/similar?pageSize=20`)

export const getPrograms = (scid) =>
  api.post(`/api/colleges/programs?from=0&createdSort=true&${scid ? `&scid=${scid}` : ''}`, {})

export const getCollegeContent = (scid, type, params = '') => api.get(`/api/colleges/${scid}/content/${type}${params}`)

export const getCollegeUrl = (scid) => `${import.meta.env.BASE_URL}/colleges/${scid}`

export const CollegeProfile = ({
  url,
  scid,
  currentAppCycle,
  section = 'overview',
  googleMapsFeatureEnabled,
  allowCollegeUpdateRequests,
  isTnTSearchFilterEnabled,
}) => {
  const myScid = useUserScid()
  const { loading, loaded, college, getCollege } = useLoadCollegeOnMount(scid)
  const isProspectOnly = getIsProspectOnly(college)
  const { LABELS, INDEX_BY_ENDPOINT } = sectionsConfiguration
  const pageLabel = LABELS[INDEX_BY_ENDPOINT[section]]
  const canCreateContent = usePermissions(permissions.MANAGE_COLLEGE_CONTENT) && allowCollegeUpdateRequests

  useTitle(`Scoir - College Profile ${pageLabel} - ${get(college, 'Name', scid)}`)

  return (
    <CollegeProfileContextWrapper
      sectionsConfiguration={sectionsConfiguration}
      isTnTSearchFilterEnabled={isTnTSearchFilterEnabled}
      url={url}
      currentAppCycle={currentAppCycle}
      section={section}
      scid={scid}
      loading={loading}
      loaded={loaded}
      college={college}
      searchPath="/colleges/search"
      topNavigationHeightTrackerId={TOP_NAVIGATION_HEIGHT_ID}
      isProspectOnly={isProspectOnly}
      getCollegesPath={getCollegeUrl}
      hostedRootPath={import.meta.env.BASE_URL}
      getCollege={getCollege}
      getSessions={getSessions}
      getPrograms={getPrograms}
      getSimilarColleges={getSimilarColleges}
      getCollegeContent={getCollegeContent}
      hideLocationMap={!googleMapsFeatureEnabled}
    >
      <ErrorBoundary>
        <LoadingState>
          <ErrorBoundary>
            <ProfileHeader
              scid={scid}
              pageHeaderActions={allowCollegeUpdateRequests && <RequestUpdateAction scid={scid} />}
              isTnTSearchFilterEnabled={isTnTSearchFilterEnabled}
              stickyHeaderActions={
                allowCollegeUpdateRequests && (
                  <RequestUpdateAction
                    scid={scid}
                    isResponsive
                  />
                )
              }
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <ProfileRoutes sections={sectionsConfiguration.SECTIONS} />
          </ErrorBoundary>
          {canCreateContent && myScid === scid && (
            <ErrorBoundary>
              <Footer>
                <FooterContent scid={scid} />
              </Footer>
            </ErrorBoundary>
          )}
        </LoadingState>
      </ErrorBoundary>
    </CollegeProfileContextWrapper>
  )
}

CollegeProfile.propTypes = {
  currentAppCycle: PropTypes.number.isRequired,
  scid: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  section: PropTypes.string,
  googleMapsFeatureEnabled: PropTypes.bool,
  allowCollegeUpdateRequests: PropTypes.bool,
  isTnTSearchFilterEnabled: PropTypes.bool,
}

export default connect((store) => {
  return {
    currentAppCycle: selectApplicationCycle(store),
    googleMapsFeatureEnabled: isGoogleMapsFeatureEnabled(store),
    allowCollegeUpdateRequests: isCollegeUpdateRequestEnabled(store),
    isTnTSearchFilterEnabled: isTnTSearchFilterEnabled(store),
  }
})(CollegeProfile)
